import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPendingServices, getAllServicesAdmin, approveService } from '../services/api';
import { SERVICE_STATUS } from '../lib/constants';

const Admin = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('pending'); // 'pending' or 'all'
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [reason, setReason] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      if (isAuthenticated && isAdmin) {
        try {
          setLoading(true);
          const data = view === 'pending' ? await getPendingServices() : await getAllServicesAdmin();
          setServices(data);
        } catch (error) {
          console.error('Failed to fetch services:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [isAuthenticated, isAdmin, view]);

  const handleApprove = async (serviceId) => {
    try {
      setProcessingId(serviceId);
      await approveService(serviceId, 'approve');
      // Refetch services
      const data = view === 'pending' ? await getPendingServices() : await getAllServicesAdmin();
      setServices(data);
    } catch (error) {
      console.error('Failed to approve service:', error);
      alert('Failed to approve service. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (serviceId) => {
    const rejectionReason = reason[serviceId];
    if (!rejectionReason || rejectionReason.trim() === '') {
      alert('Please provide a reason for rejection.');
      return;
    }

    try {
      setProcessingId(serviceId);
      await approveService(serviceId, 'reject', rejectionReason);
      setReason({ ...reason, [serviceId]: '' });
      // Refetch services
      const data = view === 'pending' ? await getPendingServices() : await getAllServicesAdmin();
      setServices(data);
    } catch (error) {
      console.error('Failed to reject service:', error);
      alert('Failed to reject service. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Admin Access Required</h2>
          <p className="text-gray-600 mb-6">You need admin privileges to access this page.</p>
          <button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg">
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case SERVICE_STATUS.APPROVED:
        return (
          <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium" data-testid="admin-status-approved">
            Approved
          </span>
        );
      case SERVICE_STATUS.PENDING:
        return (
          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium" data-testid="admin-status-pending">
            Pending
          </span>
        );
      case SERVICE_STATUS.REJECTED:
        return (
          <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-medium" data-testid="admin-status-rejected">
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8" data-testid="admin-title">
          Admin Panel - Service Management
        </h1>

        {/* View Toggle */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex gap-4">
            <button
              onClick={() => setView('pending')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                view === 'pending' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="view-pending-button"
            >
              Pending Review ({services.filter((s) => s.status === SERVICE_STATUS.PENDING).length})
            </button>
            <button
              onClick={() => setView('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                view === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="view-all-button"
            >
              All Services ({services.length})
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-gray-600">Loading services...</p>
          </div>
        ) : services.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center" data-testid="no-services-admin">
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">All Caught Up!</h3>
            <p className="text-gray-600">
              {view === 'pending' ? 'No services pending review.' : 'No services in the system yet.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="admin-services-list">
            {services.map((service) => (
              <div key={service.id} className="bg-white rounded-lg shadow-md p-6" data-testid="admin-service-item">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900" data-testid="admin-service-name">
                        {service.name}
                      </h3>
                      {getStatusBadge(service.status)}
                    </div>
                    <p className="text-gray-600 mb-2" data-testid="admin-service-description">
                      {service.description}
                    </p>
                    <p className="text-sm text-gray-500">
                      Provider: {service.provider_name} | Submitted: {new Date(service.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm bg-gray-50 p-4 rounded">
                  <div>
                    <span className="text-gray-500">Location:</span>
                    <p className="font-medium">
                      {service.city}, {service.state} - {service.pincode}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Phone:</span>
                    <p className="font-medium">{service.contact_phone}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Cost:</span>
                    <p className="font-medium capitalize">{service.cost}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Mode:</span>
                    <p className="font-medium">{service.mode.join(', ')}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <span className="text-sm text-gray-500">Service Types:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {service.service_types.map((type, index) => (
                      <span key={index} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                        {type}
                      </span>
                    ))}
                  </div>
                </div>

                {service.status === SERVICE_STATUS.PENDING && (
                  <div className="border-t pt-4 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rejection Reason (if rejecting)
                      </label>
                      <textarea
                        value={reason[service.id] || ''}
                        onChange={(e) => setReason({ ...reason, [service.id]: e.target.value })}
                        placeholder="Provide clear feedback on what needs improvement..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={2}
                        data-testid="rejection-reason-input"
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleApprove(service.id)}
                        disabled={processingId === service.id}
                        className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50"
                        data-testid="approve-button"
                      >
                        {processingId === service.id ? 'Processing...' : '✅ Approve'}
                      </button>
                      <button
                        onClick={() => handleReject(service.id)}
                        disabled={processingId === service.id}
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50"
                        data-testid="reject-button"
                      >
                        {processingId === service.id ? 'Processing...' : '❌ Reject'}
                      </button>
                    </div>
                  </div>
                )}

                {service.status === SERVICE_STATUS.REJECTED && service.status_reason && (
                  <div className="border-t pt-4">
                    <span className="text-sm font-medium text-red-600">Rejection Reason:</span>
                    <p className="text-sm text-gray-700 mt-1">{service.status_reason}</p>
                  </div>
                )}

                {service.status === SERVICE_STATUS.APPROVED && (
                  <div className="border-t pt-4">
                    <button
                      onClick={() => navigate(`/service/${service.id}`)}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      View Public Page →
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
